import React, { useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  SALES_UNIT_SET,
  getUnitNoun,
  resolveSalesUnit,
  stockAvailability,
} from '../../util/salesUnit';
import { fitFactRows } from '../../util/answerContentCore';
import { boltCircleText } from '../../util/boltCircleCore';
import { isBusinessProfile } from '../../util/profileSeoUrl';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  listingProductImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import SectionHero from './SectionHero';
import SectionTextMaybe from './SectionTextMaybe';
// P25009-498 — hidden for now, kept for later (see the render below).
// import SectionAnswerLeadMaybe from './SectionAnswerLeadMaybe';
import SectionPackageVariantsMaybe from './SectionPackageVariantsMaybe';
import SectionFullRangeCtaMaybe from './SectionFullRangeCtaMaybe';
// import SectionFitsAlsoMaybe from './SectionFitsAlsoMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import CustomListingFields from './CustomListingFields';
import { MONTERING_TEMPLATE_KEY, servicesForListing } from '../../util/serviceTargetingCore';

import css from './ListingPage.module.css';
import { listingTown } from '../../util/listingTown';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  // Route + params of this listing's public URL. Normally the breadcrumb URL
  // (/l/:category/:make/:model/:slug/:id); the legacy /l/:slug/:id path for
  // listings that have no car make and model to put in it.
  const publicListingLink = listingPageLinkProps(currentListing, rawParams);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect {...publicListingLink} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  // SEO canonical: the old listing URLs (/l/:slug/:id and the slug-less
  // /l/:id) redirect to the breadcrumb URL, so the keyword segments are
  // preserved and search engines move the ranking over. We know we are on an
  // old URL when there is no :category param. Redirect only once the listing
  // is loaded — before that we do not know its make and model — and never for
  // the pending-approval / draft variants, which have their own handling above
  // and are not indexed anyway.
  const isBreadcrumbUrl = !!rawParams.category;
  const isNormalVariant = !isPendingApprovalVariant && !isDraftVariant;
  if (!isBreadcrumbUrl && isNormalVariant && publicListingLink.name === 'ListingPage') {
    return <NamedRedirect {...publicListingLink} search={location.search} />;
  }

  // Listings without a make/model cannot use the breadcrumb URL, but the
  // slug-less /l/:id form still redirects to the slugged /l/:slug/:id one.
  const isSlugLessUrl = !rawParams.slug;
  if (isSlugLessUrl && isNormalVariant && listingSlug) {
    return <NamedRedirect name="ListingPageLegacy" params={params} search={location.search} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  /* P25009-454 BR-1/BR-2 — a soft-deleted listing (publicData.isDeleted) is a
   * real 404 to anyone but the owner; the owner keeps a noindex'd reference
   * view. Same guard as ListingPageCarousel.js. */
  const isListingDeleted = publicData.isDeleted === true;
  /* P25009-410 — a catalogue row is reachable from its seller's profile but is
   * not on the marketplace, and thin catalogue pages are what the catalogue
   * keeps out of the index. */
  const isCatalogueListing = publicData.visibility === 'catalog';
  /* P25009-485 BR-2 — a hidden trade-in is the workshop's own stock, "never
   * to customers": the same 404 for anyone but its owner, so a guessed or
   * shared link cannot show it, or sell it. */
  const isHiddenTradein = publicData.isTradein === true && publicData.isHide === true;
  if ((isListingDeleted || isHiddenTradein) && !isOwnListing) {
    if (isAuthenticated && !currentUser) {
      // Signed in but the user is still loading: deciding now would flash a
      // 404 at the owner on every hard refresh of their own deleted listing.
      return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
    }
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <div>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </div>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingProductImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  // BR-12 — see ListingPageCarousel: no `|| 0`, because a listing that does not
  // track stock is available, not sold out.
  const currentStock = currentListing.currentStock?.attributes?.quantity;
  const schemaAvailability = stockAvailability(currentStock);

  /* --- P25009-409, the twin of the block in ListingPageCarousel.js ---
   *
   * `layoutConfig.listingPage.variantType` picks which of the two listing pages
   * renders, and the marketplace is configured for 'carousel'. That is a Console
   * setting though, not a constant, so this variant has to satisfy the same
   * acceptance criteria: the answer sentence, the fit table (which arrives on its
   * own through the shared SectionDetailsMaybe) and the seller/delivery
   * structured data. Anything changed here belongs in the carousel twin too.
   *
   * Note this variant never received P25009-377's Product-schema fixes, so its
   * JSON-LD still lacks brand, condition and sku. Those are not this story's
   * scope and are left as they are. */
  const enumLabelFor = (field, value) => {
    if (!value) return value;
    const fieldConfig = config.listing?.listingFields?.find(f => f.key === field);
    return fieldConfig?.enumOptions?.find(o => `${o.option}` === `${value}`)?.label || value;
  };
  // P25009-498 — this and priceCovers are unused while the answer sentence is
  // hidden; kept for when it returns.
  // eslint-disable-next-line no-unused-vars
  const isListingClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
  const sellerServices = ensuredAuthor.attributes?.profile?.publicData?.services;
  // P25009-484 BR-3 — only where montering is set to show for this product.
  const monteringService = servicesForListing(sellerServices, publicData).find(
    service => service.templateKey === MONTERING_TEMPLATE_KEY
  );
  const monteringOffered = !!monteringService;
  const { salesUnit: listingSalesUnit, setSize: listingSetSize } = resolveSalesUnit(publicData);
  const unitNoun = getUnitNoun(publicData);
  // eslint-disable-next-line no-unused-vars
  const priceCovers =
    listingSalesUnit === SALES_UNIT_SET
      ? `hela satsen (${listingSetSize} ${unitNoun.many})`
      : `ett ${unitNoun.one}`;
  const listingCity = listingTown(publicData) || '';
  const hasSellerPlace = !!(listingCity || publicData.county);
  const schemaPostalAddress = {
    '@type': 'PostalAddress',
    ...(listingCity ? { addressLocality: listingCity } : {}),
    ...(publicData.county ? { addressRegion: publicData.county } : {}),
    addressCountry: 'SE',
  };
  const isBusinessSeller = isBusinessProfile(ensuredAuthor);
  const schemaSeller = authorDisplayName
    ? {
        '@type': isBusinessSeller ? 'LocalBusiness' : 'Person',
        name: authorDisplayName,
        ...(isBusinessSeller && hasSellerPlace ? { address: schemaPostalAddress } : {}),
      }
    : null;
  const schemaAvailableAtOrFrom = hasSellerPlace
    ? {
        '@type': 'Place',
        ...(listingCity ? { name: listingCity } : {}),
        address: schemaPostalAddress,
      }
    : null;
  const shippingSubunits = publicData.shippingPriceInSubunitsOneItem;
  const schemaShippingDetails =
    publicData.shippingEnabled && shippingSubunits != null && price?.currency
      ? {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: (Number(shippingSubunits) / 100).toFixed(2),
            currency: price.currency,
          },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'SE' },
        }
      : null;
  const schemaAddOns = monteringOffered
    ? [
        {
          '@type': 'Offer',
          name: 'Montering',
          ...(monteringService?.price != null && price?.currency
            ? { price: Number(monteringService.price).toFixed(2), priceCurrency: price.currency }
            : {}),
          itemOffered: {
            '@type': 'Service',
            name: 'Montering och balansering',
            ...(schemaSeller ? { provider: schemaSeller } : {}),
          },
        },
      ]
    : [];
  const eanDigits = String(publicData.ean || '').replace(/\D/g, '');
  const schemaGtinMaybe =
    eanDigits.length === 13 ? { gtin13: eanDigits } : eanDigits ? { gtin: eanDigits } : {};
  const schemaAdditionalProperty = fitFactRows(publicData, { labelFor: enumLabelFor }).map(row => ({
    '@type': 'PropertyValue',
    name: row.label,
    value: row.value,
  }));

  const getSEOMetaProps = () => {
    const { listingFields } = config.listing;
    const getLabel = (field, value) => {
      const fieldConfig = listingFields.find(f => f.key === field);
      if (fieldConfig && fieldConfig.enumOptions) {
        const option = fieldConfig.enumOptions.find(o => o.option === value);
        return option ? option.label : value;
      }
      return value;
    };

    const conditionLabel = getLabel('condition', condition) || '';
    /* `tiretype`, all lower case, is Console's key. This read `tireType` — a
     * key no listing carries — so the tyre type was always blank here
     * (P25009-470 BR-5). */
    const tireTypeLabel = getLabel('tiretype', publicData.tiretype) || '';
    const carBrandLabel = getLabel('categoryLevel1', publicData.categoryLevel1) || '';
    const carModelLabel = getLabel('categoryLevel2', publicData.categoryLevel2) || '';
    const carbrand = carBrandLabel;
    const carmodel = carModelLabel;
    const tirecombination =
      tireWidthFront && profileFront && diameter
        ? `${tireWidthFront}/${profileFront}R${diameter}`
        : '';
    const city = listingTown(publicData) || '';
    const county = publicData.county || '';

    const seoTitle = `${title} | Wheelplace`;
    let seoDescription = '';

    const {
      numberOfBolts,
      boltCircle,
      diameter: rimDiameter,
      hubHole,
      etFront,
      threaddepth,
    } = publicData;

    if (listingType === 'wheel' || listingType === 'business-wheel') {
      const product = 'kompletta hjul';
      seoDescription = `Köp ${product} som passar ${carbrand} ${carmodel}, ${conditionLabel}. ${tireTypeLabel} i ${tirecombination} med Mönsterdjup ${threaddepth} mm. Finns i ${city}${county ? `, ${county}` : ''
        }. Kontakta ${authorDisplayName} för mer information och frågor.`;
    } else if (listingType === 'tire' || listingType === 'businesstire') {
      seoDescription = `Köp ${tireTypeLabel} i ${tirecombination}, ${conditionLabel} med Mönsterdjup ${threaddepth} mm. Finns i ${city}${county ? `, ${county}` : ''
        }. Kontakta ${authorDisplayName} för mer information och frågor.`;
    } else if (listingType === 'rim' || listingType === 'businesswheel') {
      // Console's key "1397" is 139.7 (P25009-488).
      const boltpattern =
        numberOfBolts && boltCircle ? `${numberOfBolts}/${boltCircleText(boltCircle) || boltCircle}` : '';
      seoDescription = `Köp fälgar i ${rimDiameter} tum ${boltpattern}, ET ${etFront}, Navhål ${hubHole}, ${conditionLabel}. Finns i ${city}${county ? `, ${county}` : ''
        }. Kontakta ${authorDisplayName} för mer information och frågor.`;
    } else {
      seoDescription = description;
    }

    return { title: seoTitle, description: seoDescription };
  };

  const { title: seoTitle, description: seoDescription } = getSEOMetaProps();

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  return (
    <Page
      title={seoTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={seoDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      // Only the owner ever gets this far with a deleted listing (P25009-454
      // BR-2) — their reference view must not stay in Google's index.
      noindex={isListingDeleted || isCatalogueListing}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: seoDescription,
        name: seoTitle,
        image: schemaImages,
        ...schemaGtinMaybe,
        ...(schemaAdditionalProperty.length
          ? { additionalProperty: schemaAdditionalProperty }
          : {}),
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
          ...(schemaSeller ? { seller: schemaSeller } : {}),
          ...(schemaAvailableAtOrFrom ? { availableAtOrFrom: schemaAvailableAtOrFrom } : {}),
          ...(schemaShippingDetails ? { shippingDetails: schemaShippingDetails } : {}),
          ...(schemaAddOns.length ? { addOn: schemaAddOns } : {}),
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <SectionHero
          title={title}
          listing={currentListing}
          isOwnListing={isOwnListing}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
          imageCarouselOpen={imageCarouselOpen}
          onImageCarouselClose={() => setImageCarouselOpen(false)}
          handleViewPhotosClick={handleViewPhotosClick}
          onManageDisableScrolling={onManageDisableScrolling}
          noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
        />
        <div className={css.contentWrapperForHeroLayout}>
          <div className={css.mainColumnForHeroLayout}>
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>

            {/* P25009-498 — hidden for now, kept for later: uncomment this and
              * the import to bring it back.
              *
              * P25009-409 BR-2 / AC-1 — the generated answer sentence, above the
              * seller's own text so the quotable facts come first.
            <SectionAnswerLeadMaybe
              publicData={publicData}
              priceLabel={formattedPrice}
              priceCovers={priceCovers}
              city={listingCity}
              sellerName={authorDisplayName}
              monteringOffered={monteringOffered}
              deliveryDays={publicData.deliveryDays}
              availability={isListingClosed ? 'sold_out' : 'in_stock'}
            />
            */}
            {/* P25009-407 AC-3 — the dealer's other packages for this car,
              * which in practice are their price tiers. Renders nothing unless
              * there is something to switch to, so an ordinary listing is
              * untouched. */}
            <SectionPackageVariantsMaybe
              listingId={currentListing.id?.uuid}
              publicData={publicData}
              currentPrice={
                currentListing.attributes?.price?.amount
                  ? currentListing.attributes.price.amount / 100
                  : null
              }
            />
            {/* P25009-406 AC-2 — the way through to this dealer's full range
              * for this car. A program publishes only a few packages per car;
              * the rest of the range lives on their profile. */}
            <SectionFullRangeCtaMaybe
              publicData={publicData}
              authorId={currentAuthor?.id?.uuid}
              authorDisplayName={authorDisplayName}
            />

            <SectionTextMaybe text={description} showAsIngress />

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            {/* P25009-498 — "Passar även" hidden for now, kept for later:
              * uncomment this, the import and the fetch in ListingPage.duck.js
              * loadData to bring it back.
              *
              * P25009-398 AC-2 — also on this variant, because which layout is
              * live is decided by the hosted Console asset, not by this repo.
              * Loaded in loadData since P25009-409, so it is server-rendered.
            <SectionFitsAlsoMaybe listingId={currentListing?.id?.uuid} />
            */}

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForHeroLayout}>
            <OrderPanel
              className={css.orderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name="ListingPage"
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onContactUser={onContactUser}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
